import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { CruiseVideos, DropDown } from '../../assests/ExportImageIcon'
import MainTab from '../../componants/mainTab/MainTab'
import PopularDestination from '../popularDestination/PopularDestination'
import SpecialOffer from '../specialOffer/SpecialOffer'
import InfluencerReels from '../influencerReels/InfluencerReels'
import GetApp from '../getApp/GetApp'
import OurLatestBlog from '../ourLatestblog/OurLatestBlog'
import CruiseSearchWizard from './searchModals/CruiseSearchWizard'
import './cruise.css'
import service from '../../services/service'
import endpoints from '../../services/endpoints'
import { showToast } from '../../componants/toast/toastService'
import { getClientIp } from '../../utils/clientIp'
import { setCruisePorts, setCruiseLines, setCruiseShips, setCruiseResults, setCruiseSearchParams, setCruiseSearching } from '../../redux/cruiseSearch/cruiseSearchSlice'

const MONTH_NAMES_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

// No month is pre-selected by default anymore (see the month useState below) — kept
// here in case a "default to next month" fallback is wanted again later.
// const getDefaultMonth = () => {
//     const d = new Date()
//     d.setMonth(d.getMonth() + 1)
//     return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
// }

const formatMonthLabel = (monthStr) => {
  if (!monthStr) return ''
  const [year, month] = monthStr.split('-').map(Number)
  return `${MONTH_NAMES_FULL[month - 1]} ${year}`
}

const CruiseMain = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const ports = useSelector((state) => state.cruiseSearch.ports)
  const lines = useSelector((state) => state.cruiseSearch.lines)
  const ships = useSelector((state) => state.cruiseSearch.ships)
  const savedParams = useSelector((state) => state.cruiseSearch.searchParams)

  const [portsLoading, setPortsLoading] = useState(false)
  const [linesLoading, setLinesLoading] = useState(false)
  const [shipsLoading, setShipsLoading] = useState(false)

  // Confirmed values shown as a summary in the search pill — committed when the
  // wizard's "Done" step runs. The actual search only fires from the "Search
  // Cruise" button on this page, not from inside the wizard.
  const [destinationSelection, setDestinationSelection] = useState(savedParams.destinationPort || null)
  const [embarkationSelection, setEmbarkationSelection] = useState(savedParams.embarkationPort || null)
  const [lineSelection, setLineSelection] = useState(savedParams.cruiseLines || [])
  const [shipSelection, setShipSelection] = useState(savedParams.shipIds || [])
  const [month, setMonth] = useState(savedParams.month || '')

  const [wizardOpen, setWizardOpen] = useState(false)
  const [wizardStep, setWizardStep] = useState(0)

  useEffect(() => {
    if (ports.length) return
    setPortsLoading(true)
    getClientIp()
      .then((ip) => service.post(endpoints.cruises.ports, { ip_address: ip }, { silent: true }))
      .then(({ data }) => {
        dispatch(setCruisePorts(data.data || []))
      })
      .catch(() => {
        showToast('Unable to load cruise ports right now.', 'error')
      })
      .finally(() => setPortsLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (lines.length) return
    setLinesLoading(true)
    getClientIp()
      .then((ip) => service.post(endpoints.cruises.lines, { ip_address: ip }, { silent: true }))
      .then(({ data }) => {
        dispatch(setCruiseLines(data.data || []))
      })
      .catch(() => {
        showToast('Unable to load cruise lines right now.', 'error')
      })
      .finally(() => setLinesLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (ships.length) return
    setShipsLoading(true)
    getClientIp()
      .then((ip) => service.post(endpoints.cruises.ships, { ip_address: ip }, { silent: true }))
      .then(({ data }) => {
        dispatch(setCruiseShips(data.data || []))
      })
      .catch(() => {
        showToast('Unable to load cruise ships right now.', 'error')
      })
      .finally(() => setShipsLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const openWizard = (step = 0) => {
    setWizardStep(step)
    setWizardOpen(true)
  }

  // Runs the actual search using the values the wizard just confirmed (passed
  // explicitly rather than read off component state, since state set just before
  // this call hasn't committed yet).
  const runSearch = (values) => {
    dispatch(setCruiseSearchParams({
      destinationPort: values.destinationSelection,
      embarkationPort: values.embarkationSelection,
      month: values.month,
      cruiseLines: values.lineSelection,
      shipIds: values.shipSelection,
    }))
    dispatch(setCruiseResults(null))
    dispatch(setCruiseSearching(true))
    navigate('/cruise-list')

    // Search runs in the background; the list page shows a loader
    // (driven by isSearching) until results land in the store.
    //
    // Step-by-step at search time, in the documented API order:
    // 1) ports  2) cruise lines  3) ships  4) the actual search call.
    // Each lookup is skipped if already cached (usually already loaded on
    // page mount), so a repeat search doesn't re-fetch master data.
    ;(async () => {
      try {
        const ip = await getClientIp()

        if (!ports.length) {
          const portsRes = await service.post(endpoints.cruises.ports, { ip_address: ip }, { silent: true })
          dispatch(setCruisePorts(portsRes.data.data || []))
        }
        if (!lines.length) {
          const linesRes = await service.post(endpoints.cruises.lines, { ip_address: ip }, { silent: true })
          dispatch(setCruiseLines(linesRes.data.data || []))
        }
        if (!ships.length) {
          const shipsRes = await service.post(endpoints.cruises.ships, { ip_address: ip }, { silent: true })
          dispatch(setCruiseShips(shipsRes.data.data || []))
        }

        // Every /cruises/search field is sent explicitly (not left to server defaults).
        // destination_port_ids / embarkation_ports_ids / cruise_lines / cruise_ships_ids
        // are all UI-backed (via the search wizard). The rest (nights range, cabin count,
        // price range, marketing/vendor codes, search type) have no matching UI in this
        // design yet, so they're sent as the documented defaults.
        const payload = {
          ip_address: ip,
          start_date: values.month,
          end_date: values.month,
          from_nights: '',
          to_nights: '',
          destination_port_ids: values.destinationSelection?.portIds?.length ? values.destinationSelection.portIds : ['ALL'],
          cruise_lines: values.lineSelection.length ? values.lineSelection : ['ALL'],
          cruise_ships_ids: values.shipSelection.length ? values.shipSelection : ['ALL'],
          embarkation_ports_ids: values.embarkationSelection?.portIds?.length ? values.embarkationSelection.portIds : ['ALL'],
          port_of_call_ids: ['ALL'],
          number_of_cabin: '2',
          marketing_code: '',
          vendor_sailing_identifier: '',
          from_price: 0,
          to_price: 9999999,
          search_type: 'cruiseOnly',
        }
        // eslint-disable-next-line no-console
        console.log('[cruise search] payload sent to /cruises/search:', payload)
        const { data } = await service.post(endpoints.cruises.search, payload, { timeout: 45000 })
        dispatch(setCruiseResults(data.data))
      } catch {
        // error toast already shown by the service interceptor
      } finally {
        dispatch(setCruiseSearching(false))
      }
    })()
  }

  // Wizard "Done" only commits the picked values to the search pill — it does
  // not trigger a search itself.
  const handleWizardDone = (values) => {
    setDestinationSelection(values.destinationSelection)
    setEmbarkationSelection(values.embarkationSelection)
    setLineSelection(values.lineSelection)
    setShipSelection(values.shipSelection)
    setMonth(values.month)
  }

  // The "Search Cruise" button on this page is the only thing that actually runs a search.
  const handleSearchClick = () => {
    runSearch({ destinationSelection, embarkationSelection, lineSelection, shipSelection, month })
  }

  return (
    <>
        <section className='banner'>
            <div className='videoPart'>
                <video className='promoBanner' autoPlay loop muted playsinline="" data-object-fit="cover" preload="metadata">
                    <source src={CruiseVideos} type="video/mp4" />
                </video>
            </div>
            <div className='bannerContent'>
                <div className='container'>
                    <div className='row g-4'>
                        <div className='col-md-12'>
                            <h1>Book Your Cruise With Just One Click</h1>
                            <MainTab />
                        </div>
                        <div className='col-md-12'>
                            <div className='bookingAreaanner'>

                                <div className='cruiseSearchPill'>
                                    <div className='cruiseSearchPill__field' onClick={() => openWizard(0)}>
                                        <span className='cruiseSearchPill__label'>Cruising To</span>
                                        <div className='cruiseSearchPill__valueRow'>
                                            <span className={destinationSelection ? '' : 'cruiseSearchPill__placeholder'}>
                                                {destinationSelection?.label || (portsLoading ? 'Loading...' : 'Anywhere')}
                                            </span>
                                        </div>
                                    </div>
                                    {/* <div className='cruiseSearchPill__field' onClick={() => openWizard(0)}>
                                        <span className='cruiseSearchPill__label'>Leaving From</span>
                                        <div className='cruiseSearchPill__valueRow'>
                                            <span className={embarkationSelection ? '' : 'cruiseSearchPill__placeholder'}>
                                                {embarkationSelection?.label || (portsLoading ? 'Loading...' : 'Any port')}
                                            </span>
                                        </div>
                                    </div> */}
                                    <div className='cruiseSearchPill__field' onClick={() => openWizard(0)}>
                                        <span className='cruiseSearchPill__label'>Leaving On</span>
                                        <div className='cruiseSearchPill__valueRow'>
                                            <span className={month ? '' : 'cruiseSearchPill__placeholder'}>
                                                {month ? formatMonthLabel(month) : 'Select month'}
                                            </span>
                                            <img src={DropDown} alt="" />
                                        </div>
                                    </div>
                                    <button type='button' className='commonBtn cruiseSearchPill__btn' onClick={handleSearchClick}>Search Cruise</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <PopularDestination />

        <SpecialOffer />

        <InfluencerReels />

        <GetApp />

        <OurLatestBlog />

        <CruiseSearchWizard
            open={wizardOpen}
            onClose={() => setWizardOpen(false)}
            ports={ports}
            portsLoading={portsLoading}
            lines={lines}
            linesLoading={linesLoading}
            ships={ships}
            shipsLoading={shipsLoading}
            initialStepIndex={wizardStep}
            initialValues={{ destinationSelection, embarkationSelection, lineSelection, shipSelection, month }}
            onDone={handleWizardDone}
        />
    </>
  )
}

export default CruiseMain
